import { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Select, { components } from 'react-select'
import { useThemeMode } from '../_metronic/partials'

export interface SingleSelectOption {
  label: string
  value: string
  /** Secondary muted text shown after the label, e.g. '77 ASINs 142 SKUs' */
  meta?: string
  submenu?: SingleSelectOption[]
}

interface SingleSelectedButtonProps {
  options: SingleSelectOption[]
  value: SingleSelectOption | null
  onChange: (option: SingleSelectOption) => void
  placeholder?: string
  /** Icon rendered at the start of the control, e.g. a filter funnel icon */
  icon?: React.ReactNode
  isDisabled?: boolean
  /** A className (e.g. 'w-150px') or a pixel width number (e.g. 120) */
  widthClass?: string | number
  /** Control height in px. Defaults to 38 to match the app's toolbar filter fields. */
  heightclass?: number
  /** Gap in px between the selected text and the dropdown arrow. Defaults to 8. */
  arrowGap?: number
  /** Font size for the selected value / placeholder text, e.g. 12 or '10px'. Defaults to 12. */
  fontsize?: number | string
  /** Extra className(s) appended alongside the width className. */
  className?: string
  /** Menu width: px number, or a CSS value such as '100%' to match the control. Defaults to 220. */
  menuWidth?: number | string
  closeMenuOnSelect?: boolean
  /** Where the menu opens. 'auto' flips it above the control when there is no room below. */
  menuPlacement?: 'auto' | 'bottom' | 'top'
  /** Sort options (and submenus) A to Z by label. Defaults to true; set false to keep the given order. */
  sortAlphabetically?: boolean
  /**
   * 'toolbar' (default): the compact, centered filter look used in page headers.
   * 'form': a standard form field for modals/forms — left-aligned, 42px, neutral input
   * border (no focus highlight). Defaults height to 42, font size to 13 and menu width to 100%.
   */
  variant?: 'toolbar' | 'form'
  /** Red border for a failed validation (form variant). */
  isInvalid?: boolean
  /** Show a search box at the top of the menu. Defaults to true; set false to hide it. */
  showSearch?: boolean
  searchPlaceholder?: string
}

const compareByLabel = (a: SingleSelectOption, b: SingleSelectOption) =>
  (a.label ?? '').localeCompare(b.label ?? '', undefined, { numeric: true, sensitivity: 'base' })

// Options with an empty value (e.g. a static "All channels" option) stay pinned at the top.
const sortOptionsByLabel = (options: SingleSelectOption[]): SingleSelectOption[] => {
  const pinned = options.filter((opt) => opt.value === '')
  const rest = options.filter((opt) => opt.value !== '').sort(compareByLabel)
  return [...pinned, ...rest].map((opt) =>
    opt.submenu ? { ...opt, submenu: sortOptionsByLabel(opt.submenu) } : opt
  )
}

const filterOptionsByLabel = (options: SingleSelectOption[], term: string): SingleSelectOption[] =>
  options.reduce<SingleSelectOption[]>((acc, opt) => {
    if (opt.label?.toLowerCase().includes(term)) {
      acc.push(opt)
    } else if (opt.submenu) {
      const submenu = filterOptionsByLabel(opt.submenu, term)
      if (submenu.length) acc.push({ ...opt, submenu })
    }
    return acc
  }, [])

const SingleSelectMenuList = (props: any) => {
  const {
    showSearch,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    onSearchMouseDown,
    onSearchBlur,
    onMenuClose,
  } = props.selectProps
  return (
    <>
      {showSearch && (
        <div
          className='checkbox-filter-search'
          style={{ margin: '6px 6px 0' }}
          // Keep react-select from grabbing focus/keys so the input can be typed into
          onMouseDown={(e) => {
            e.stopPropagation()
            onSearchMouseDown()
          }}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.6'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle cx='7' cy='7' r='5.5' />
            <path d='m14 14-3.2-3.2' />
          </svg>
          <input
            type='text'
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Escape') onMenuClose()
            }}
            onBlur={onSearchBlur}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      <components.MenuList {...props}>{props.children}</components.MenuList>
    </>
  )
}

const SingleSelectControl = (props: any) => (
  <components.Control {...props}>
    {props.selectProps.customIcon}
    {props.children}
  </components.Control>
)

const SingleSelectDropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='14'
      height='14'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      className={`checkbox-filter-caret ${
        props.selectProps.menuIsOpen ? 'checkbox-filter-caret-open' : ''
      }`}
    >
      <path d='m6 9 6 6 6-6' />
    </svg>
  </components.DropdownIndicator>
)

const SingleSelectIndicatorSeparator = () => null

const CheckIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='14'
    height='14'
    viewBox='0 0 16 16'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M3 8.5 6.2 12 13 4' />
  </svg>
)

const SingleSelectOptionRow = ({
  data,
  innerRef,
  innerProps,
  selectOption,
  isSelected,
  isFocused,
  selectProps,
}: any) => {
  const { mode } = useThemeMode()
  const [open, setOpen] = useState(false)
  const optionRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const panelClass = mode === 'dark' ? 'checkbox-filter-panel-dark' : ''

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (data.submenu) {
      const rect = optionRef.current?.getBoundingClientRect()
      if (rect) setPosition({ top: rect.top, left: rect.right })
      setOpen((prev) => !prev)
    } else {
      selectOption(data)
      selectProps?.onMenuClose?.()
    }
  }

  const handleMouseEnter = () => {
    if (!data.submenu) return
    if (closeTimeout.current) clearTimeout(closeTimeout.current)

    const rect = optionRef.current?.getBoundingClientRect()
    if (rect) setPosition({ top: rect.top, left: rect.right })
    setOpen(true)
  }

  const handleMouseLeave = () => {
    if (!data.submenu) return
    closeTimeout.current = setTimeout(() => {
      setOpen(false)
    }, 200)
  }

  const handleSubmenuMouseEnter = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current)
  }

  const handleSubmenuMouseLeave = () => {
    closeTimeout.current = setTimeout(() => {
      setOpen(false)
    }, 200)
  }

  const handleSubmenuClick = (sub: SingleSelectOption) => {
    selectOption(sub)
    setOpen(false)
    selectProps?.onMenuClose?.()
  }

  return (
    <div
      ref={(el) => {
        if (innerRef && 'current' in innerRef) innerRef.current = el
        optionRef.current = el
      }}
      {...innerProps}
      className={`checkbox-filter-option ${
        isFocused ? 'checkbox-filter-option-focused' : ''
      } ${panelClass}`}
      style={{ position: 'relative', userSelect: 'none', pointerEvents: 'none' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className='clickable-area'
        onClick={handleClick}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          pointerEvents: 'auto',
        }}
      >
        <span>
          {data.label}
          {data.meta && <span className='text-muted fs-8 ms-2'>{data.meta}</span>}
        </span>
        {data.submenu ? (
          <svg
            style={{
              marginLeft: '5px',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(-90deg)' : 'rotate(0deg)',
            }}
            height='18'
            width='18'
            viewBox='0 0 20 20'
            aria-hidden='true'
            focusable='false'
            fill='currentColor'
          >
            <path d='M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z'></path>
          </svg>
        ) : (
          isSelected && <CheckIcon />
        )}
      </div>

      {open &&
        data.submenu &&
        createPortal(
          <div
            className={`checkbox-filter-panel ${panelClass}`}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: 180,
              zIndex: 9999,
              padding: 6,
            }}
            onMouseEnter={handleSubmenuMouseEnter}
            onMouseLeave={handleSubmenuMouseLeave}
            onClick={(e) => e.stopPropagation()}
          >
            {data.submenu.map((sub: SingleSelectOption) => (
              <div
                key={sub.value}
                className='checkbox-filter-option'
                onClick={() => handleSubmenuClick(sub)}
              >
                <span>{sub.label}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  )
}

/**
 * Reusable single-select filter button (react-select based) styled to match
 * the app's toolbar filter fields: 38px height, 6px radius, 12px/500 text.
 * Supports an optional two-level submenu per option.
 */
const SingleSelectedButton = ({
  options,
  value,
  onChange,
  placeholder = 'Select',
  icon,
  isDisabled,
  widthClass = 'w-150px',
  heightclass,
  arrowGap = 8,
  fontsize,
  className = '',
  menuWidth,
  closeMenuOnSelect = false,
  menuPlacement = 'bottom',
  sortAlphabetically = true,
  variant = 'toolbar',
  isInvalid = false,
  showSearch = true,
  searchPlaceholder = 'Search...',
}: SingleSelectedButtonProps) => {
  const { mode } = useThemeMode()
  const [menuOpen, setMenuOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  // react-select closes the menu when its own input blurs; skip that one close when the
  // blur happens because the user clicked into the search box.
  const ignoreNextCloseRef = useRef(false)
  // Inside a react-bootstrap Modal the menu must render within the modal: its focus trap pulls
  // focus back from anything outside, which would blur the search box and close the menu.
  const [portalTarget, setPortalTarget] = useState<HTMLElement>(document.body)
  const isInModal = portalTarget !== document.body

  const openMenu = () => {
    const modal = wrapperRef.current?.closest<HTMLElement>('.modal')
    setPortalTarget(modal ?? document.body)
    setMenuOpen(true)
  }

  const closeMenu = () => {
    if (ignoreNextCloseRef.current) {
      ignoreNextCloseRef.current = false
      return
    }
    setMenuOpen(false)
    setSearch('')
  }

  const handleSearchMouseDown = () => {
    if (!(document.activeElement as HTMLElement | null)?.closest('.checkbox-filter-search')) {
      ignoreNextCloseRef.current = true
    }
  }

  // Focus leaving the search box: stay open if it moved back into the select (option/control
  // click refocuses react-select's input), otherwise it was a click outside — close.
  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const next = e.relatedTarget as Node | null
    if (next && wrapperRef.current?.contains(next)) return
    closeMenu()
  }
  const isForm = variant === 'form'
  const height = heightclass ?? (isForm ? 42 : 38)
  const fontSize = fontsize ?? (isForm ? 13 : 12)
  const menuWidthValue = menuWidth ?? (isForm ? '100%' : 220)
  const formBorderColor = isInvalid ? 'var(--bs-danger)' : 'var(--bs-gray-300)'
  const displayOptions = useMemo(
    () => (sortAlphabetically ? sortOptionsByLabel(options) : options),
    [options, sortAlphabetically]
  )
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!showSearch || !term) return displayOptions
    return filterOptionsByLabel(displayOptions, term)
  }, [displayOptions, search, showSearch])
  const isNumericWidth = typeof widthClass === 'number'
  const innerHeight = height - 2

  return (
    <div ref={wrapperRef} style={{ display: 'contents' }}>
      <Select
        value={value}
        className={isNumericWidth ? `py-0 ${className}` : `${widthClass} py-0 ${className}`}
        options={filteredOptions}
        onChange={(opt: any) => onChange(opt)}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isSearchable={false}
        closeMenuOnSelect={closeMenuOnSelect}
        menuIsOpen={menuOpen}
        onMenuOpen={openMenu}
        onMenuClose={closeMenu}
        noOptionsMessage={() => 'No options found'}
        menuPortalTarget={portalTarget}
        menuPlacement={menuPlacement}
        // The .modal element is position: fixed, so fixed positioning keeps the menu under the control
        menuPosition={isInModal ? 'fixed' : 'absolute'}
        // @ts-ignore custom props consumed by SingleSelectControl / SingleSelectMenuList
        customIcon={icon}
        showSearch={showSearch}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={searchPlaceholder}
        onSearchMouseDown={handleSearchMouseDown}
        onSearchBlur={handleSearchBlur}
        components={{
          Option: SingleSelectOptionRow,
          Control: SingleSelectControl,
          DropdownIndicator: SingleSelectDropdownIndicator,
          IndicatorSeparator: SingleSelectIndicatorSeparator,
          MenuList: SingleSelectMenuList,
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            ...(mode === 'dark'
              ? {
                  primary50: 'none',
                  primary: 'white',
                  primary25: 'none',
                  neutral0: '#1b1b29',
                  neutral80: 'white',
                }
              : {}),
          },
        })}
        styles={{
          container: (base) => ({
            ...base,
            ...(isNumericWidth ? { width: widthClass, minWidth: widthClass } : {}),
          }),
          control: (base) => ({
            ...base,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            minHeight: height,
            height,
            borderRadius: 6,
            border: mode === 'light' ? '1px solid #e2e8f0' : '1px solid #323248',
            boxShadow:
              mode === 'light' ? '0 1px 3px 0 #0000001a, 0 1px 2px -1px #0000001a' : 'none',
            backgroundColor: mode === 'light' ? 'transparent' : '#1b1b29',
            outline: 'none',
            ':hover': {
              border:
                mode === 'light'
                  ? '1px solid #e2e8f0'
                  : `1px solid ${isDisabled ? '#323248' : '#009ef7'}`,
            },
            ...(isForm && {
              border: `1px solid ${formBorderColor}`,
              boxShadow: 'none',
              backgroundColor: mode === 'light' ? '#fff' : 'transparent',
              transition: 'border-color 0.15s ease',
              ':hover': {
                border: `1px solid ${isInvalid ? formBorderColor : 'var(--bs-gray-400)'}`,
              },
            }),
          }),
          valueContainer: (base) => ({
            ...base,
            height: innerHeight,
            padding: isForm ? '0 12px' : '0 8px',
            justifyContent: isForm ? 'flex-start' : 'center',
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: innerHeight,
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: '#99a1b7',
            paddingLeft: arrowGap,
            paddingRight: 8,
            ':hover': { color: '#99a1b7' },
          }),
          // Menu is portalled to <body>; keep it above Bootstrap modals (z-index 1055)
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
          menu: (base) => ({
            ...base,
            width: menuWidthValue,
            borderRadius: 6,
            border: `1px solid ${mode === 'dark' ? '#2b2b40' : '#e2e8f0'}`,
            boxShadow: '0 6px 16px 0 rgba(0,0,0,0.12)',
            overflow: 'hidden',
          }),
          menuList: (base) => ({
            ...base,
            padding: 6,
          }),
          singleValue: (base) => ({
            ...base,
            position: 'static',
            transform: 'none',
            maxWidth: '100%',
            fontSize,
            fontWeight: 500,
            lineHeight: '20px',
            color: isForm ? 'var(--bs-gray-800)' : mode === 'light' ? '#4b5675' : base.color,
            textAlign: isForm ? 'left' : 'center',
            flex: 1,
            marginLeft: 0,
            marginRight: 0,
          }),
          placeholder: (base) => ({
            ...base,
            position: 'static',
            transform: 'none',
            maxWidth: '100%',
            fontSize,
            fontWeight: isForm ? 400 : 500,
            lineHeight: '20px',
            textAlign: isForm ? 'left' : 'center',
            ...(isForm && { color: 'var(--bs-gray-500)' }),
            flex: 1,
            marginLeft: 0,
            marginRight: 0,
          }),
          input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
            gridTemplateColumns: '0px',
            outline: 'none',
            boxShadow: 'none',
          }),
          option: (base) => ({
            ...base,
            cursor: 'pointer',
          }),
        }}
      />
    </div>
  )
}

export { SingleSelectedButton }
